import { Tag, Typography } from 'antd';
import { EnvironmentOutlined, CalendarOutlined, UserOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { format } from '@/theme/format';
import { semantic } from '@/theme/tokens';
import type { CurrencyCode, ProjectPhase } from '@/mocks/types';

const { Title, Text } = Typography;

export interface ProjectHeroBannerProps {
  /** Project code (GA-26-001) */
  code: string;
  /** Project display name */
  name: string;
  /** Optional Arabic name shown below the EN name when locale is AR */
  nameAr?: string;
  /** Client display name */
  clientName: string;
  /** City label */
  city: string;
  /** Project lifecycle phase */
  phase: ProjectPhase;
  /** Contract value (numeric) */
  contractValue: number;
  /** Currency for the contract value */
  currency: CurrencyCode;
  /** Project manager name */
  manager?: string;
  /** Project start date (ISO) */
  startDate?: string;
  /** Project end date (ISO) */
  endDate?: string;
}

/**
 * Hero banner for the project detail drawer.
 *
 * Replaces the bare "code — name" Drawer title with a full-width strip
 * that gives the project context at a glance: phase pill, location pin,
 * project manager, schedule, and contract value. Visual treatment uses a
 * brand-coloured gradient (no external imagery) layered with stylized
 * landscape SVG strokes for atmosphere — ~3 KB inline, no CDN cost.
 */
export default function ProjectHeroBanner({
  code,
  name,
  nameAr,
  clientName,
  city,
  phase,
  contractValue,
  currency,
  manager,
  startDate,
  endDate,
}: ProjectHeroBannerProps) {
  const { t, i18n } = useTranslation(['engineering', 'dashboard', 'common']);
  const isRtl = i18n.language === 'ar';

  const phaseColor = (() => {
    switch (phase) {
      case 'inception':
        return semantic.brand.accent;
      case 'execution':
        return semantic.brand.primary;
      case 'closeOut':
        return semantic.status.success;
      case 'warranty':
        return semantic.status.info;
    }
  })();

  return (
    <div
      style={{
        position: 'relative',
        margin: '-24px -24px 24px',
        padding: '24px 24px 20px',
        background: `linear-gradient(135deg, ${semantic.brand.primary} 0%, ${semantic.brand.primaryActive} 100%)`,
        color: '#fff',
        overflow: 'hidden',
      }}
    >
      {/* Atmospheric stylized horizon — sand-coloured strokes hinting at
       * a landscape silhouette. Pure SVG. */}
      <svg
        aria-hidden="true"
        viewBox="0 0 1000 200"
        preserveAspectRatio="xMidYEnd slice"
        style={{
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
          opacity: 0.18,
          pointerEvents: 'none',
        }}
      >
        {/* Distant rolling hills */}
        <path
          d="M0 160 C 200 130, 400 145, 600 130 C 750 120, 900 140, 1000 125 L 1000 200 L 0 200 Z"
          fill={semantic.brand.accent}
          opacity="0.5"
        />
        {/* Closer ridge */}
        <path
          d="M0 180 C 250 155, 480 170, 720 155 C 860 148, 950 168, 1000 160 L 1000 200 L 0 200 Z"
          fill={semantic.brand.secondary}
          opacity="0.4"
        />
        {/* Stylized palm silhouettes — small, far-right */}
        <g transform="translate(880, 100)" opacity="0.55">
          <line x1="0" y1="0" x2="0" y2="80" stroke={semantic.brand.secondary} strokeWidth="2" />
          <path
            d="M0 0 C -16 -6, -28 0, -36 8 M0 0 C 16 -6, 28 0, 36 8 M0 0 C -8 -14, -2 -18, 6 -16 M0 0 C 8 -12, 16 -8, 18 0"
            stroke={semantic.brand.secondary}
            strokeWidth="2"
            fill="none"
          />
        </g>
      </svg>

      <div style={{ position: 'relative', zIndex: 1 }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 16,
            flexWrap: 'wrap',
          }}
        >
          <div style={{ flex: 1, minWidth: 240 }}>
            <Text
              style={{
                fontSize: 11,
                letterSpacing: 1.5,
                textTransform: 'uppercase',
                color: 'rgba(255, 255, 255, 0.7)',
                fontWeight: 600,
                display: 'block',
                marginBottom: 6,
              }}
            >
              {code}
            </Text>
            <Title
              level={3}
              style={{ color: '#fff', margin: 0, fontSize: 22, lineHeight: 1.2 }}
            >
              {isRtl && nameAr ? nameAr : name}
            </Title>
            {isRtl && nameAr && (
              <Text style={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: 12 }}>{name}</Text>
            )}
            <div
              style={{
                display: 'flex',
                gap: 10,
                marginTop: 10,
                flexWrap: 'wrap',
                alignItems: 'center',
              }}
            >
              <Tag
                color={phaseColor}
                style={{
                  margin: 0,
                  fontWeight: 600,
                  border: `1px solid ${phaseColor}`,
                  background: 'rgba(255, 255, 255, 0.16)',
                  color: '#fff',
                }}
              >
                {t(`dashboard:phase.${phase}`)}
              </Tag>
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 4,
                  fontSize: 12,
                  color: 'rgba(255, 255, 255, 0.78)',
                }}
              >
                <EnvironmentOutlined /> {city}
              </span>
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 4,
                  fontSize: 12,
                  color: 'rgba(255, 255, 255, 0.78)',
                }}
              >
                <UserOutlined /> {clientName}
              </span>
              {manager && (
                <span
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 4,
                    fontSize: 12,
                    color: 'rgba(255, 255, 255, 0.78)',
                  }}
                >
                  PM: {manager}
                </span>
              )}
              {(startDate || endDate) && (
                <span
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 4,
                    fontSize: 12,
                    color: 'rgba(255, 255, 255, 0.78)',
                  }}
                >
                  <CalendarOutlined />
                  {startDate ? format.date(startDate, 'short') : '—'}
                  {' → '}
                  {endDate ? format.date(endDate, 'short') : '—'}
                </span>
              )}
            </div>
          </div>
          <div
            style={{
              textAlign: isRtl ? 'left' : 'right',
              minWidth: 160,
              flexShrink: 0,
            }}
          >
            <Text
              style={{
                fontSize: 11,
                letterSpacing: 0.8,
                textTransform: 'uppercase',
                color: 'rgba(255, 255, 255, 0.65)',
                fontWeight: 500,
                display: 'block',
              }}
            >
              {t('engineering:project.kpi.contractValue')}
            </Text>
            <div
              style={{
                fontSize: 26,
                fontWeight: 700,
                color: '#fff',
                lineHeight: 1.2,
                marginTop: 2,
              }}
            >
              {format.currency(contractValue, currency, { fractionDigits: 0 })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
